import {
  getSeriesTitle,
  getSiteAverageTitle,
  getSiteRangeTitle,
  isLinkedSiteAverage,
  isLinkedSiteRange,
  menuReducer,
  MenuType
} from "reducers/chartMenuReducer";
import { ChartTypeMenu } from "components/DataExplorationV2/ChartTypeMenu";
import { ConfigureMenu } from "components/DataExplorationV2/ConfigureMenu/ConfigureMenu";
import { EditMenu } from "components/DataExplorationV2/EditMenu/EditMenu";
import { GraphConfigBar } from "components/DataExplorationV2/GraphConfigBar";
import { styled } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import {
  ChartSubtype,
  ChartType
} from "components/DataExplorationV2/ChartSubtypes";
import { DataExplorationChart } from "reducers/configurableChartReducer";
import {
  LineSeries,
  ScadaDatetimeTagDataSource,
  Axis,
  ReferenceLine
} from "interfaces/chart";
import { Tag } from "interfaces/tag";
import { Machine } from "interfaces/machine";
import { useMachinesQuery } from "hooks/queries/useMachinesQuery";
import { useGetComponents } from "hooks/components/useGetComponents";
import { useGetTags } from "hooks/tags/useGetTags";
import { v4 as createUUID } from "uuid";
import {
  useSelectableSeries,
  useSelectedMachine,
  useSelectedParameter,
  useSelectedReferenceLine,
  useSelectedSeries,
  useSelectedYAxis
} from "hooks/data-exploration-v2/chart-menu-hooks";
import { withTransientProps } from "core/utils/transientProps";

export interface ChartMenuProps {
  chart: DataExplorationChart;
  onChangePointsEnabled: (enabled: boolean) => void;
  onChangeTitle: (title: string) => void;
  onAddSeries: (seriesId: string, pointsEnabled: boolean) => void;
  onAddSiteAverage: (
    parentId: string,
    seriesId: string,
    title: string,
    tagId: number,
    siteId: number,
    pointsEnabled: boolean,
    xAxisId: string,
    yAxisId: string
  ) => void;
  onAddSiteRange: (
    parentId: string,
    seriesId: string,
    title: string,
    tagId: number,
    siteId: number,
    xAxisId: string,
    yAxisId: string
  ) => void;
  onChangeMachineOnSeries: (
    seriesId: string,
    title: string,
    machineId: number | null,
    tagId: number | null
  ) => void;
  onChangeParameterOnSeries: (
    seriesId: string,
    title: string,
    machineId: number | null,
    tagId: number | null,
    tagName: string | null
  ) => void;
  onUpdateSiteAverage: (
    seriesId: string,
    title: string,
    siteId: number,
    tagId: number
  ) => void;
  onUpdateSiteRange: (
    seriesId: string,
    title: string,
    siteId: number,
    tagId: number
  ) => void;
  onChangeXAxisLabelEnabled: (enabled: boolean) => void;
  onChangeStartDate: (startDate: Date | null) => void;
  onChangeEndDate: (endDate: Date | null) => void;
  onChangeYAxisMin: (yAxisId: string, min: number | null) => void;
  onChangeYAxisMax: (yAxisId: string, max: number | null) => void;
  onChangeYAxisLabelEnabled: (yAxisId: string) => void;
  onDeleteSeries: (...seriesIds: string[]) => void;
  onDeleteAllSeries: () => void;
  onResetXAxesRange: () => void;
  onChangeReferenceLineAxis: (
    axis: Axis | null,
    selectedReferenceLineId: string | null
  ) => void;
  onChangeReferenceLineValue: (
    value: string | number | Date | null,
    selectedReferenceLineId: string | null
  ) => void;
  onAddReferenceLine: (newId: string) => void;
  onDeleteReferenceLine: (referenceLineId: string) => void;
  onChangeReferenceLineTitle: (
    referenceLine: ReferenceLine,
    title: string | null
  ) => void;
}

export const ChartMenu = ({
  chart,
  onChangePointsEnabled,
  onChangeTitle,
  onAddSeries,
  onAddSiteAverage,
  onAddSiteRange,
  onChangeMachineOnSeries,
  onChangeParameterOnSeries,
  onUpdateSiteAverage,
  onUpdateSiteRange,
  onChangeXAxisLabelEnabled,
  onChangeStartDate,
  onChangeEndDate,
  onChangeYAxisMin,
  onChangeYAxisMax,
  onChangeYAxisLabelEnabled,
  onDeleteSeries,
  onDeleteAllSeries,
  onResetXAxesRange,
  onChangeReferenceLineAxis,
  onChangeReferenceLineValue,
  onAddReferenceLine,
  onDeleteReferenceLine,
  onChangeReferenceLineTitle
}: ChartMenuProps): JSX.Element => {
  const [state, dispatch] = useReducer(menuReducer, {
    selectedMenu: MenuType.CONFIGURE,
    chartType: ChartType.LINE,
    chartSubtype: ChartSubtype.LINE_WITH_MARKERS,
    selectedSeriesId: null,
    selectedYAxisId: null,
    selectedReferenceLineId: null
  });

  const ref = useRef<HTMLDivElement>(null);
  const menuOpen = state.selectedMenu !== MenuType.NONE;
  const pointsEnabled = state.chartSubtype === ChartSubtype.LINE_WITH_MARKERS;

  const selectableSeries = useSelectableSeries(chart.series);
  const selectedSeries = useSelectedSeries(
    state.selectedSeriesId,
    selectableSeries
  );
  const selectedYAxis = useSelectedYAxis(state.selectedYAxisId, chart.yAxes);
  const siteAverageEnabled = useMemo(
    () =>
      chart.series.some(series =>
        isLinkedSiteAverage(series, state.selectedSeriesId)
      ),
    [chart.series, state.selectedSeriesId]
  );

  const siteRangeEnabled = useMemo(
    () =>
      chart.series.some(series =>
        isLinkedSiteRange(series, state.selectedSeriesId)
      ),
    [chart.series, state.selectedSeriesId]
  );

  const selectedReferenceLine = useSelectedReferenceLine(
    state.selectedReferenceLineId,
    chart.plotLines
  );

  const {
    data: _machines,
    isLoading: machinesLoading,
    isError: machinesError
  } = useMachinesQuery();

  // ensure variable is defined for typescript
  const machines = useMemo(() => _machines?.results ?? [], [_machines]);

  const selectedMachine = useSelectedMachine(selectedSeries, machines);

  const { components, componentsLoading, componentsError } = useGetComponents();
  const { tags, loading: tagsLoading, error: tagsError } = useGetTags();
  const selectedParameter = useSelectedParameter(selectedSeries, tags);

  const handleAddSeries = useCallback(() => {
    const newSeriesId = createUUID();
    onAddSeries(newSeriesId, pointsEnabled);
    dispatch({ type: "changeSelectedSeries", seriesId: newSeriesId });
  }, [onAddSeries, pointsEnabled]);

  const handleChangeSelectedReferenceLine = (
    line: ReferenceLine | null
  ): void => {
    dispatch({
      type: "changeSelectedReferenceLineId",
      selectedReferenceLineId: line?.id ?? null
    });
  };

  // First series must be selected when the user come to this page.
  useEffect(() => {
    if (!selectedSeries && selectableSeries.length > 0) {
      handleChangeSelectedSeries(selectableSeries[0]);
    } else if (selectableSeries.length === 0) {
      handleAddSeries();
    }
  }, [handleAddSeries, selectableSeries, selectedSeries]);

  useEffect(() => {
    if (selectedSeries !== null) {
      dispatch({
        type: "changeSelectedYAxisId",
        selectedYAxisId: selectedSeries.yAxisId
      });
    }
  }, [selectedSeries]);

  // First Y-Axis must be selected when nothing is selected among them
  useEffect(() => {
    if (chart.yAxes?.length > 0 && !selectedYAxis) {
      dispatch({
        type: "changeSelectedYAxisId",
        selectedYAxisId: chart.yAxes[0].id
      });
    }
  }, [chart.yAxes, selectedYAxis]);

  const handleAddReferenceLine = useCallback(() => {
    const newPlotId = createUUID();
    onAddReferenceLine(newPlotId);

    handleChangeSelectedReferenceLine({
      id: newPlotId,
      targetAxis: null,
      title: "...",
      color: "black",
      dashStyle: "dash",
      value: null,
      width: 2
    });
  }, [onAddReferenceLine]);

  const handleDeleteReferenceLine = (referenceLineId: string): void => {
    if (referenceLineId === state.selectedReferenceLineId) {
      handleChangeSelectedReferenceLine(null);
    }
    onDeleteReferenceLine(referenceLineId);
  };

  const handleChangeChartType = (chartType: ChartType): void => {
    dispatch({ type: "changeChartType", chartType });
    onDeleteAllSeries();
  };

  const handleChangeSubtype = (chartSubtype: ChartSubtype): void => {
    dispatch({ type: "changeChartSubtype", chartSubtype });

    switch (chartSubtype) {
      case ChartSubtype.LINE:
        onChangePointsEnabled(false);
        return;
      case ChartSubtype.LINE_WITH_MARKERS:
        onChangePointsEnabled(true);
        return;
      case ChartSubtype.SCATTER:
        return;
      default:
        console.error("unhandled chart subtype");
    }
  };

  const handleChangeSelectedSeries = (
    series: LineSeries<ScadaDatetimeTagDataSource>
  ): void => {
    dispatch({
      type: "changeSelectedSeries",
      seriesId: series.id
    });
  };

  const updateSiteAverage = (
    parameter: Tag | null,
    machine: Machine | null
  ): void => {
    if (siteAverageEnabled) {
      const siteAverageToUpdate = chart.series.find(
        x =>
          x.parentId === state.selectedSeriesId &&
          x.dataSource.type === "scada-median"
      );

      if (selectedSeries !== null && siteAverageToUpdate) {
        if (parameter !== null && machine !== null) {
          // series is valid
          if (siteAverageToUpdate.id !== null) {
            // update it
            onUpdateSiteAverage(
              siteAverageToUpdate.id,
              getSiteAverageTitle(parameter),
              machine.site,
              parameter.id
            );
          } else {
            // create it
            const averageSeriesId = createUUID();
            const pointsEnabled =
              state.chartSubtype === ChartSubtype.LINE_WITH_MARKERS;
            onAddSiteAverage(
              selectedSeries.id,
              averageSeriesId,
              getSiteAverageTitle(parameter),
              parameter.id,
              machine.site,
              pointsEnabled,
              selectedSeries.xAxisId,
              selectedSeries.yAxisId
            );
          }
        } else {
          // the series is invalid, so we need to clear it up if it currently exists
          if (siteAverageToUpdate.id !== null) {
            onDeleteSeries(siteAverageToUpdate.id);
          }
        }
      }
    }
  };

  const updateSiteRange = (
    parameter: Tag | null,
    machine: Machine | null
  ): void => {
    if (siteRangeEnabled) {
      const siteRangeToUpdate = chart.series.find(
        x =>
          x.parentId === state.selectedSeriesId &&
          x.dataSource.type === "scada-range"
      );

      if (selectedSeries !== null && siteRangeToUpdate) {
        if (parameter !== null && machine !== null) {
          // series is valid
          if (siteRangeToUpdate.id !== null) {
            // update it
            onUpdateSiteRange(
              siteRangeToUpdate.id,
              getSiteRangeTitle(parameter),
              machine.site,
              parameter.id
            );
          } else {
            // create it
            const rangeSeriesId = createUUID();
            onAddSiteRange(
              selectedSeries.id,
              rangeSeriesId,
              getSiteRangeTitle(parameter),
              parameter.id,
              machine.site,
              selectedSeries.xAxisId,
              selectedSeries.yAxisId
            );
          }
        } else {
          // the series is invalid, so we need to clear it up if it currently exists
          if (siteRangeToUpdate.id !== null) {
            onDeleteSeries(siteRangeToUpdate.id);
          }
        }
      }
    }
  };

  const handleChangeMachine = (machine: Machine | null): void => {
    if (state.selectedSeriesId) {
      onChangeMachineOnSeries(
        state.selectedSeriesId,
        getSeriesTitle(selectedParameter?.name, machine?.name),
        machine?.id ?? null,
        selectedParameter?.id ?? null
      );

      updateSiteAverage(selectedParameter, machine);
      updateSiteRange(selectedParameter, machine);
    }
  };

  const handleChangeParameter = (tag: Tag | null): void => {
    if (state.selectedSeriesId) {
      onChangeParameterOnSeries(
        state.selectedSeriesId,
        getSeriesTitle(tag?.name, selectedMachine?.name),
        selectedMachine?.id ?? null,
        tag?.id ?? null,
        tag?.name ?? null
      );

      updateSiteAverage(tag, selectedMachine);
      updateSiteRange(tag, selectedMachine);
    }
  };

  const handleChangeSiteAveragedEnabled = (enabled: boolean): void => {
    if (state.selectedSeriesId !== null) {
      if (enabled && !siteAverageEnabled) {
        let averageSeriesId = null;

        if (
          selectedMachine !== null &&
          selectedParameter !== null &&
          selectedSeries !== null
        ) {
          // create the site average
          averageSeriesId = createUUID();
          onAddSiteAverage(
            selectedSeries.id,
            averageSeriesId,
            getSiteAverageTitle(selectedParameter),
            selectedParameter.id,
            selectedMachine.site,
            pointsEnabled,
            selectedSeries.xAxisId,
            selectedSeries.yAxisId
          );
        }
      } else if (!enabled) {
        // remove any site averages from the chart if they exist
        const siteAverageIdsToDelete = chart.series
          .filter(series => isLinkedSiteAverage(series, state.selectedSeriesId))
          .map(x => x.id);
        if (siteAverageIdsToDelete.length > 0) {
          onDeleteSeries(...siteAverageIdsToDelete);
        }
      }
    }
  };

  const handleChangeSiteRangeEnabled = (enabled: boolean): void => {
    if (state.selectedSeriesId !== null) {
      if (enabled && !siteRangeEnabled) {
        let rangeSeriesId = null;

        if (
          selectedMachine !== null &&
          selectedParameter !== null &&
          selectedSeries !== null
        ) {
          // create the site range
          rangeSeriesId = createUUID();
          onAddSiteRange(
            selectedSeries.id,
            rangeSeriesId,
            getSiteRangeTitle(selectedParameter),
            selectedParameter.id,
            selectedMachine.site,
            selectedSeries.xAxisId,
            selectedSeries.yAxisId
          );
        }
      } else if (!enabled) {
        // remove any site ranges from the chart if they exist
        const siteRangeIdsToDelete = chart.series
          .filter(series => isLinkedSiteRange(series, state.selectedSeriesId))
          .map(x => x.id);
        if (siteRangeIdsToDelete.length > 0) {
          onDeleteSeries(...siteRangeIdsToDelete);
        }
      }
    }
  };

  const handleDeleteSeries = (seriesId: string): void => {
    // the series is about to be deleted, so we need to deselect it
    dispatch({ type: "changeSelectedSeries", seriesId: null });

    onDeleteSeries(seriesId);
    if (selectableSeries?.length === 1) {
      // If the deleting series is the last one, we should reset the x axes range
      onResetXAxesRange();
    }
  };

  return (
    <MenuWrapper $menuOpen={menuOpen}>
      {state.selectedMenu === MenuType.CHART_TYPE && (
        <ChartTypeMenu
          chartType={state.chartType}
          chartSubtype={state.chartSubtype}
          onChangeChartType={handleChangeChartType}
          onChangeSubtype={handleChangeSubtype}
        />
      )}
      {state.selectedMenu === MenuType.CONFIGURE && (
        <ConfigureMenu
          loading={machinesLoading || tagsLoading || componentsLoading}
          seriesError={machinesError || tagsError || componentsError}
          components={components}
          parameters={tags}
          selectedParameter={selectedParameter}
          onChangeParameter={handleChangeParameter}
          machines={machines}
          selectedMachine={selectedMachine}
          onChangeMachine={handleChangeMachine}
          siteAverageEnabled={siteAverageEnabled}
          siteRangeEnabled={siteRangeEnabled}
          onChangeSiteAverageEnabled={handleChangeSiteAveragedEnabled}
          onChangeSiteRangeEnabled={handleChangeSiteRangeEnabled}
          series={selectableSeries}
          selectedSeries={selectedSeries}
          onChangeSelectedSeries={series => handleChangeSelectedSeries(series)}
          onAddSeries={handleAddSeries}
          onDeleteSeries={handleDeleteSeries}
          startDate={chart.xAxes[0]?.min ? new Date(chart.xAxes[0].min) : null}
          endDate={chart.xAxes[0]?.max ? new Date(chart.xAxes[0].max) : null}
          onChangeStartDate={onChangeStartDate}
          onChangeEndDate={onChangeEndDate}
          xAxes={chart.xAxes}
          xAxisLabelEnabled={chart.xAxes.some(axis => axis.title.enabled)}
          onChangeXAxisLabelEnabled={onChangeXAxisLabelEnabled}
          yAxes={chart.yAxes}
          selectedYAxis={selectedYAxis}
          onChangeSelectedYAxis={yAxis => {
            dispatch({
              type: "changeSelectedYAxisId",
              selectedYAxisId: yAxis.id
            });
          }}
          onChangeYAxisMin={min =>
            state.selectedYAxisId &&
            onChangeYAxisMin(state.selectedYAxisId, min)
          }
          onChangeYAxisMax={max =>
            state.selectedYAxisId &&
            onChangeYAxisMax(state.selectedYAxisId, max)
          }
          yAxisLabelEnabled={selectedYAxis?.title.enabled ?? true}
          onChangeYAxisLabelEnabled={() => {
            state.selectedYAxisId &&
              onChangeYAxisLabelEnabled(state.selectedYAxisId);
          }}
          referenceLines={chart.plotLines}
          selectedReferenceLine={selectedReferenceLine}
          onChangeSelectedReferenceLine={handleChangeSelectedReferenceLine}
          onChangeReferenceLineAxis={axis =>
            onChangeReferenceLineAxis(axis, state.selectedReferenceLineId)
          }
          onChangeReferenceLineValue={value =>
            onChangeReferenceLineValue(value, state.selectedReferenceLineId)
          }
          onAddReferenceLine={handleAddReferenceLine}
          onDeleteReferenceLine={handleDeleteReferenceLine}
          onChangeReferenceLineTitle={onChangeReferenceLineTitle}
        />
      )}
      {state.selectedMenu === MenuType.EDIT && (
        <EditMenu title={chart.title} onChangeTitle={onChangeTitle} />
      )}
      <GraphConfigBar
        ref={ref}
        selectedMenu={state.selectedMenu}
        onClickMenuButton={value =>
          dispatch({
            type: "changeMenu",
            selectedMenu: value
          })
        }
      />
    </MenuWrapper>
  );
};

const MenuWrapper = styled("div", withTransientProps)<{ $menuOpen: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: flex-start;
`;
